import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AboutMetric } from '../../models/about-metrics.model';

/**
 * Animated headline-metric card for the redesigned About page.
 *
 * Matches the approved prototype: a monospace value with the magnitude suffix
 * (e.g. "M+") rendered separately in yellow. The number counts up from 0 to the
 * display magnitude once the card scrolls into view, with an ease-out curve.
 *
 * Robustness (the prototype's sole IntersectionObserver could leave a card
 * showing "0"):
 *   - Background tabs: animation is skipped and the final value rendered, since
 *     requestAnimationFrame is suspended while hidden.
 *   - The count uses wall-clock elapsed time, so a throttled rAF snaps straight
 *     to the final value when it resumes rather than freezing mid-count.
 *   - A safety timer renders the final value if the observer never fires.
 *   - `prefers-reduced-motion` and missing IntersectionObserver render the final
 *     value immediately.
 */
@Component({
  selector: 'pla-metric-card',
  templateUrl: './metric-card.component.html',
  styleUrls: ['./metric-card.component.scss'],
})
export class MetricCardComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public metric!: AboutMetric;
  @Input() public duration = 1400; // ms
  @Input() public onDark = false; // light text variant for dark backgrounds

  @ViewChild('valueEl', { static: true }) private valueEl!: ElementRef<HTMLElement>;

  /** Number portion shown in the value (animated); excludes the suffix. */
  public rendered = '0';
  /** Magnitude suffix (e.g. "M+", "k+", "+"), rendered statically in yellow. */
  public suffix = '';

  private targetNum = 0;
  private decimals = 0;
  private countable = false;

  private observer: IntersectionObserver | null = null;
  private rafId: number | null = null;
  private fallbackTimer: number | null = null;
  private hasAnimated = false;

  constructor(private zone: NgZone, private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    if (!this.metric) return;
    this.parseDisplay();
    // Seed so a non-counting metric (e.g. "Since 1989") never flashes "0", and a
    // counting one starts from a formatted zero.
    this.rendered = this.countable ? this.formatCount(0) : this.valuePart();
  }

  ngAfterViewInit(): void {
    if (!this.metric) return;

    if (
      !this.countable ||
      this.prefersReducedMotion() ||
      typeof IntersectionObserver === 'undefined'
    ) {
      this.renderFinal();
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasAnimated) {
              this.hasAnimated = true;
              this.clearFallback();
              this.animate();
              this.disconnect();
            }
          });
        },
        { threshold: 0.35 },
      );
      this.observer.observe(this.host.nativeElement);

      // Safety net: if the observer never fires (layout quirks, a tab that never
      // gains focus, etc.) show the real value rather than leaving a "0".
      this.fallbackTimer = window.setTimeout(() => {
        if (!this.hasAnimated) {
          this.hasAnimated = true;
          this.disconnect();
          this.renderFinal();
        }
      }, this.duration + 2500);
    });
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.clearFallback();
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
  }

  private animate(): void {
    // rAF is suspended in hidden tabs — don't start a count that would stick at
    // "0". Show the final value; it's correct the moment the tab is seen.
    if (typeof document !== 'undefined' && document.hidden) {
      this.renderFinal();
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - start) / this.duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      this.setNumber(this.formatCount(this.targetNum * eased));
      if (t < 1) {
        this.rafId = requestAnimationFrame(step);
      } else {
        this.renderFinal();
      }
    };
    this.rafId = requestAnimationFrame(step);
  }

  private renderFinal(): void {
    this.setNumber(this.valuePart());
  }

  private setNumber(text: string): void {
    this.rendered = text;
    if (this.valueEl?.nativeElement) {
      this.valueEl.nativeElement.textContent = text;
    }
  }

  /**
   * Splits the vetted display (e.g. "79M+", "375k+", "Since 1989") into the
   * value portion shown in the number and the magnitude suffix shown in yellow.
   * The count-up animates toward the display magnitude (79), not the precise
   * underlying value, so the running number matches the final figure exactly.
   */
  private parseDisplay(): void {
    const d = (this.metric.display || '').trim();
    this.countable = /^\d/.test(d);
    if (!this.countable) {
      this.suffix = '';
      this.targetNum = 0;
      this.decimals = 0;
      return;
    }
    const m = d.match(/^([\d.,]+)(.*)$/);
    const valueText = m ? m[1] : d;
    this.suffix = m ? m[2] : '';
    this.targetNum = parseFloat(valueText.replace(/,/g, ''));
    this.decimals = (valueText.split('.')[1] || '').length;
  }

  /** The non-suffix portion of the display, e.g. "79" or "Since 1989". */
  private valuePart(): string {
    const d = (this.metric.display || '').trim();
    return this.countable ? d.slice(0, d.length - this.suffix.length) : d;
  }

  private formatCount(n: number): string {
    return n.toLocaleString('en-GB', {
      minimumFractionDigits: this.decimals,
      maximumFractionDigits: this.decimals,
    });
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  private disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private clearFallback(): void {
    if (this.fallbackTimer !== null) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }
}
